package com.schoolconnect.app.feature.staff

import com.schoolconnect.app.datasource.network.response.SchoolStaffWithAssignmentsResponse
import com.schoolconnect.app.feature.common.ServerPagedListUiState
import com.schoolconnect.app.feature.common.ServerPagingState
import com.schoolconnect.app.feature.common.compareBySmartText
import com.schoolconnect.app.feature.common.toWindow
import com.schoolconnect.app.repository.systemlookup.SystemLookupOptions
import com.schoolconnect.app.util.isValidEmail
import com.schoolconnect.app.util.isValidMobileNumber

data class StaffFilterFormValues(
    val searchQuery: String = "",
    val staffRole: String = "",
    val authRole: String = "",
    val gender: String = "",
    val status: String = "",
    val createdFrom: String = "",
    val createdTo: String = ""
)

data class StaffFormValues(
    val staffId: String = "",
    val staffName: String = "",
    val staffRole: String = "",
    val authRole: String = "",
    val mobileNumber: String = "",
    val gender: String = "",
    val emailAddress: String = "",
    val status: String = "Active",
    val teachingAssignments: List<StaffTeachingAssignmentUi> = emptyList()
)

data class StaffTeachingAssignmentUi(
    val classSectionId: String = "",
    val subjectIds: List<String> = emptyList(),
    val isClassTeacher: Boolean = false
)

data class StaffClassSectionOption(
    val id: String,
    val label: String
)

data class StaffSubjectOption(
    val id: String,
    val label: String
)

data class StaffFormValidationErrors(
    val staffId: String = "",
    val staffName: String = "",
    val staffRole: String = "",
    val authRole: String = "",
    val mobileNumber: String = "",
    val gender: String = "",
    val emailAddress: String = "",
    val status: String = "",
    val teachingAssignments: String = ""
) {
    val hasAnyError: Boolean get() = listOf(staffId, staffName, staffRole, authRole, mobileNumber, gender, emailAddress, status, teachingAssignments).any { it.isNotBlank() }
}

internal fun validateStaffEditor(values: StaffFormValues): StaffFormValidationErrors = StaffFormValidationErrors(
    staffId = if (values.staffId.trim().isBlank()) "Staff ID is required." else "",
    staffName = if (values.staffName.trim().isBlank()) "Staff name is required." else "",
    staffRole = if (values.staffRole.trim().isBlank()) "Staff role is required." else "",
    authRole = if (values.authRole.trim().isBlank()) "Auth role is required." else "",
    mobileNumber = when {
        values.mobileNumber.trim().isBlank() -> "Mobile number is required."
        !isValidMobileNumber(values.mobileNumber.trim()) -> "Enter a valid mobile number."
        else -> ""
    },
    gender = if (values.gender.trim().isBlank()) "Gender is required." else "",
    emailAddress = when {
        values.emailAddress.trim().isBlank() -> "Email address is required."
        !isValidEmail(values.emailAddress) -> "Enter a valid email address."
        else -> ""
    },
    status = if (values.status.trim().isBlank()) "Status is required." else "",
    teachingAssignments = values.teachingAssignments
        .map { it.classSectionId.trim() }
        .filter { it.isNotBlank() }
        .let { classSectionIds ->
            if (classSectionIds.distinct().size != classSectionIds.size) "A class & section can only be assigned once." else ""
        }
)

data class StaffAuthRoleOption(
    val code: String,
    val label: String
)

enum class StaffSortField(val apiField: String) {
    STAFF_ID("employee_id"),
    STAFF_NAME("name"),
    STAFF_ROLE("role_title"),
    AUTH_ROLE("auth_role"),
    MOBILE_NUMBER("mobile"),
    STATUS("is_active")
}

enum class StaffStatusUi {
    ACTIVE,
    INACTIVE
}

enum class StaffRoleUi {
    TEACHER,
    PRINCIPAL,
    COORDINATOR,
    LIBRARIAN,
    ACCOUNTANT,
    VICE_PRINCIPAL,
    COUNSELOR,
    SECURITY,
    LAB_ASSISTANT,
    OTHER
}

enum class StaffAuthRoleUi {
    STAFF,
    ADMIN,
    MANAGEMENT
}

enum class StaffGenderUi {
    MALE,
    FEMALE,
    OTHER
}

data class StaffRowUi(
    val id: String,
    val staffId: String,
    val staffName: String,
    val staffRole: StaffRoleUi,
    val roleTitle: String = "",
    val authRole: StaffAuthRoleUi,
    val authRoleCode: String = "",
    val mobileNumber: String,
    val gender: StaffGenderUi,
    val emailAddress: String,
    val status: StaffStatusUi,
    val createdBy: String = "School Administrator",
    val createdAt: String = "",
    val updatedBy: String = "School Administrator",
    val updatedAt: String = "",
    val viewAssignments: List<StaffViewAssignmentUi> = emptyList()
)

data class StaffViewAssignmentUi(
    val classLabel: String,
    val subjectLabel: String,
    val isClassTeacher: Boolean = false
)

data class StaffUiState(
    val isLoading: Boolean = false,
    val errorMessage: String = "",
    val infoMessage: String = "",
    override val paging: ServerPagingState = ServerPagingState(),
    val isFilterModalOpen: Boolean = false,
    val isAddStaffModalOpen: Boolean = false,
    val isEditStaffModalOpen: Boolean = false,
    val editingStaffId: String = "",
    val editingStaffFormValues: StaffFormValues? = null,
    val isEditingStaffLoading: Boolean = false,
    val isViewStaffModalOpen: Boolean = false,
    val viewingStaffId: String = "",
    val searchQuery: String = "",
    val filters: StaffFilterFormValues = StaffFilterFormValues(),
    val sortField: StaffSortField = StaffSortField.STAFF_ID,
    val sortAscending: Boolean = true,
    val staff: List<StaffRowUi> = emptyList(),
    val isSaving: Boolean = false,
    val authRoleOptions: List<StaffAuthRoleOption> = emptyList(),
    val isAuthRolesLoading: Boolean = false,
    val classSectionOptions: List<StaffClassSectionOption> = emptyList(),
    val subjectOptions: List<StaffSubjectOption> = emptyList(),
    val subjectOptionsByClassSectionId: Map<String, List<StaffSubjectOption>> = emptyMap(),
    val isAssignmentOptionsLoading: Boolean = false,
    val assignmentOptionsError: String = ""
) : ServerPagedListUiState

internal fun staffFilterLabels(filters: StaffFilterFormValues): List<String> = buildList {
    if (filters.staffRole.isNotBlank()) add("Staff role: ${filters.staffRole.trim()}")
    if (filters.authRole.isNotBlank()) add("Auth role: ${filters.authRole.trim()}")
    if (filters.gender.isNotBlank()) add("Gender: ${filters.gender.trim()}")
    if (filters.status.isNotBlank()) add("Status: ${filters.status.trim()}")
    if (filters.createdFrom.isNotBlank() || filters.createdTo.isNotBlank()) {
        val from = filters.createdFrom.ifBlank { "Any" }
        val to = filters.createdTo.ifBlank { "Any" }
        add("Created: $from to $to")
    }
}

sealed interface StaffUiEvent {
    data object BackClicked : StaffUiEvent
    data class SearchChanged(val query: String) : StaffUiEvent
    data object FilterModalOpened : StaffUiEvent
    data object FilterModalClosed : StaffUiEvent
    data class FiltersApplied(val values: StaffFilterFormValues) : StaffUiEvent
    data object FiltersReset : StaffUiEvent
    data class SortChanged(val field: StaffSortField) : StaffUiEvent
    data class PageChanged(val pageIndex: Int) : StaffUiEvent
    data class PageSizeChanged(val pageSize: Int) : StaffUiEvent
    data object RefreshRequested : StaffUiEvent
    data object AddStaffClicked : StaffUiEvent
    data object AddStaffModalClosed : StaffUiEvent
    data object AssignmentOptionsRetryRequested : StaffUiEvent
    data class AddStaffSubmitted(val values: StaffFormValues) : StaffUiEvent
    data class EditStaff(val staffId: String) : StaffUiEvent
    data object EditStaffModalClosed : StaffUiEvent
    data class EditStaffSubmitted(val staffId: String, val values: StaffFormValues) : StaffUiEvent
    data class ViewStaff(val staffId: String) : StaffUiEvent
    data object ViewStaffModalClosed : StaffUiEvent
    data class ToggleStatus(val staffId: String) : StaffUiEvent
    data class DeleteStaff(val staffId: String) : StaffUiEvent
    data object ClearMessages : StaffUiEvent
}

internal data class StaffPageUi(
    val items: List<StaffRowUi>,
    val totalCount: Int,
    val startIndex: Int,
    val endIndex: Int,
    val pageCount: Int
)

internal fun staffStatusLabel(status: StaffStatusUi): String = when (status) {
    StaffStatusUi.ACTIVE -> "Active"
    StaffStatusUi.INACTIVE -> "Inactive"
}

internal fun staffRoleLabel(role: StaffRoleUi): String = when (role) {
    StaffRoleUi.TEACHER -> "Teacher"
    StaffRoleUi.PRINCIPAL -> "Principal"
    StaffRoleUi.COORDINATOR -> "Coordinator"
    StaffRoleUi.LIBRARIAN -> "Librarian"
    StaffRoleUi.ACCOUNTANT -> "Accountant"
    StaffRoleUi.VICE_PRINCIPAL -> "Vice Principal"
    StaffRoleUi.COUNSELOR -> "Counselor"
    StaffRoleUi.SECURITY -> "Security"
    StaffRoleUi.LAB_ASSISTANT -> "Lab Assistant"
    StaffRoleUi.OTHER -> "Other"
}

internal fun staffAuthRoleLabel(role: StaffAuthRoleUi): String = when (role) {
    StaffAuthRoleUi.STAFF -> "Support Staff"
    StaffAuthRoleUi.ADMIN -> "Admin"
    StaffAuthRoleUi.MANAGEMENT -> "Management"
}

internal fun staffGenderLabel(gender: StaffGenderUi): String =
    SystemLookupOptions.label("GENDER", gender.code).takeIf { it.isNotBlank() && it != gender.code }
        ?: when (gender) {
            StaffGenderUi.MALE -> "Male"
            StaffGenderUi.FEMALE -> "Female"
            StaffGenderUi.OTHER -> "Other"
        }

internal fun staffGenderInputLabel(value: String): String {
    val normalized = value.trim()
    if (normalized.isBlank()) return ""
    SystemLookupOptions.label("GENDER", normalized)
        .takeIf { it.isNotBlank() && !it.equals(normalized, ignoreCase = true) }
        ?.let { return it }
    return when (normalized.lowercase()) {
        "m", "male" -> "Male"
        "f", "female" -> "Female"
        "o", "other" -> "Other"
        else -> normalized
    }
}

internal fun staffRoleOptions(): List<String> = StaffRoleUi.entries.map(::staffRoleLabel)
internal fun staffAuthRoleOptions(): List<String> = StaffAuthRoleUi.entries.map(::staffAuthRoleLabel)
internal fun defaultStaffAuthRoleOptions(): List<StaffAuthRoleOption> = listOf(
    StaffAuthRoleOption("SCHOOL_ADMIN", "Admin"),
    StaffAuthRoleOption("SCHOOL_MGMT", "Management"),
    StaffAuthRoleOption("SCHOOL_SUPPORT_STAFF", "Support Staff"),
    StaffAuthRoleOption("SCHOOL_TEACHER", "Teacher")
)
internal fun staffAuthRoleOptionsFromApi(
    roles: List<com.schoolconnect.app.datasource.network.response.RoleResponse>
): List<StaffAuthRoleOption> = roles.mapNotNull { role ->
    val code = role.roleName?.trim()?.uppercase().orEmpty()
    if (code !in setOf(
            "SCHOOL_ADMIN",
            "SCHOOL_MGMT",
            "SCHOOL_SUPPORT_STAFF",
            "SCHOOL_TEACHER"
        )
    ) return@mapNotNull null
    code.takeIf { it.isNotBlank() }?.let {
        StaffAuthRoleOption(
            code = it,
            label = staffAuthRoleDisplayLabel(it)
        )
    }
}.distinctBy { it.code.uppercase() }

internal fun canonicalStaffAuthRoleCode(value: String): String = when (value.trim().uppercase()) {
    "TEACHER", "SCHOOL TEACHER", "SCHOOL_TEACHER" -> "SCHOOL_TEACHER"
    "STAFF", "SUPPORT STAFF", "SCHOOL SUPPORT STAFF", "SCHOOL_SUPPORT_STAFF" -> "SCHOOL_SUPPORT_STAFF"
    "ADMIN", "SCHOOL ADMIN", "SCHOOL_ADMIN" -> "SCHOOL_ADMIN"
    "MANAGEMENT", "SCHOOL MANAGEMENT", "SCHOOL_MGMT" -> "SCHOOL_MGMT"
    else -> value.trim()
}

internal fun staffAuthRoleDisplayLabel(value: String, fallback: StaffAuthRoleUi? = null): String = when (
    canonicalStaffAuthRoleCode(value).uppercase()
) {
    "SCHOOL_TEACHER" -> "Teacher"
    "SCHOOL_SUPPORT_STAFF" -> "Support Staff"
    "SCHOOL_ADMIN" -> "Admin"
    "SCHOOL_MGMT" -> "Management"
    else -> fallback?.let(::staffAuthRoleLabel).orEmpty().ifBlank { value.trim() }
}
internal fun staffGenderOptions(): List<String> = SystemLookupOptions.labels("GENDER")

private val StaffGenderUi.code: String
    get() = when (this) {
        StaffGenderUi.MALE -> "M"
        StaffGenderUi.FEMALE -> "F"
        StaffGenderUi.OTHER -> "O"
    }
internal fun staffStatusOptions(): List<String> = StaffStatusUi.entries.map(::staffStatusLabel)

internal fun defaultStaffFormValues(): StaffFormValues = StaffFormValues(status = "Active")

internal fun staffAuthRoleCode(
    roleLabel: String,
    options: List<StaffAuthRoleOption> = emptyList()
): String {
    // An empty filter means "any auth role". Do not apply the teacher
    // fallback here; that would make an unselected Auth Role filter active.
    if (roleLabel.trim().isBlank()) return ""
    options.firstOrNull {
        it.label.equals(roleLabel.trim(), ignoreCase = true) ||
            it.code.equals(roleLabel.trim(), ignoreCase = true)
    }?.let { return canonicalStaffAuthRoleCode(it.code) }
    return canonicalStaffAuthRoleCode(roleLabel)
}

internal fun staffStatusIsActive(statusLabel: String): Boolean = when (statusLabel.trim().lowercase()) {
    "inactive" -> false
    else -> true
}

internal fun staffFormValuesFrom(
    row: StaffRowUi,
    options: List<StaffAuthRoleOption> = emptyList()
): StaffFormValues = StaffFormValues(
    staffId = row.staffId,
    staffName = row.staffName,
    staffRole = row.roleTitle.ifBlank { staffRoleLabel(row.staffRole) },
    authRole = options.firstOrNull { it.code.equals(row.authRoleCode, ignoreCase = true) }?.label
        ?: staffAuthRoleLabel(row.authRole),
    mobileNumber = row.mobileNumber,
    gender = staffGenderLabel(row.gender),
    emailAddress = row.emailAddress,
    status = staffStatusLabel(row.status)
)

internal fun staffFormValuesFrom(
    response: SchoolStaffWithAssignmentsResponse,
    options: List<StaffAuthRoleOption> = emptyList()
): StaffFormValues {
    val base = StaffFormValues(
        staffId = response.employeeId.orEmpty(),
        staffName = response.name.orEmpty(),
        staffRole = response.roleTitle.orEmpty().ifBlank { "Other" },
        authRole = options.firstOrNull { it.code.equals(response.authRole.orEmpty(), ignoreCase = true) }?.label
            ?: response.authRole.orEmpty(),
        mobileNumber = response.mobile.orEmpty(),
        gender = staffGenderInputLabel(response.gender.orEmpty()),
        emailAddress = response.email.orEmpty(),
        status = if (response.isActive == true) "Active" else "Inactive"
    )
    val byClass = linkedMapOf<String, MutableList<String>>()
    response.teachingAssignments.orEmpty().forEach { assignment ->
        val classSectionId = assignment.classSectionId.orEmpty().trim()
        if (classSectionId.isNotBlank() && assignment.isActive != false) {
            byClass.getOrPut(classSectionId) { mutableListOf() }
            assignment.subjectId.orEmpty().trim().takeIf { it.isNotBlank() }?.let {
                byClass.getValue(classSectionId).add(it)
            }
        }
    }
    val classTeacherIds = response.classTeacherAssignments.orEmpty()
        .filter { it.isActive != false }
        .mapNotNull { it.classSectionId?.trim()?.takeIf(String::isNotBlank) }
        .toSet()
    classTeacherIds.forEach { byClass.getOrPut(it) { mutableListOf() } }
    return base.copy(
        teachingAssignments = byClass.map { (classSectionId, subjectIds) ->
            StaffTeachingAssignmentUi(classSectionId, subjectIds.distinct(), classSectionId in classTeacherIds)
        }
    )
}

internal fun staffViewAssignmentsFrom(
    response: SchoolStaffWithAssignmentsResponse
): List<StaffViewAssignmentUi> {
    val assignmentsByClass = linkedMapOf<String, Pair<String, MutableList<String>>>()
    response.teachingAssignments.orEmpty()
        .filter { it.isActive != false }
        .forEach { assignment ->
            val classLabel = listOf(assignment.className, assignment.sectionName)
                .mapNotNull { it?.trim()?.takeIf(String::isNotBlank) }
                .joinToString(" - ")
                .ifBlank { "—" }
            val subjectLabel = listOf(assignment.subjectName, assignment.subjectShortCode)
                .mapNotNull { it?.trim()?.takeIf(String::isNotBlank) }
                .firstOrNull()
                ?: "—"
            val classKey = assignment.classSectionId?.trim().takeUnless { it.isNullOrBlank() }
                ?: classLabel
            val (_, subjects) = assignmentsByClass.getOrPut(classKey) { classLabel to mutableListOf() }
            if (subjectLabel !in subjects) subjects.add(subjectLabel)
        }

    val classTeacherAssignments = linkedMapOf<String, StaffViewAssignmentUi>()
    response.classTeacherAssignments.orEmpty()
        .filter { it.isActive != false }
        .forEach { assignment ->
            val classLabel = listOf(assignment.className, assignment.sectionName)
                .mapNotNull { it?.trim()?.takeIf(String::isNotBlank) }
                .joinToString(" - ")
                .ifBlank { "—" }
            val classKey = assignment.classSectionId?.trim().takeUnless { it.isNullOrBlank() }
                ?: classLabel
            classTeacherAssignments.getOrPut(classKey) {
                StaffViewAssignmentUi(classLabel, "Class Teacher", isClassTeacher = true)
            }
        }

    return assignmentsByClass.map { (_, assignment) ->
        StaffViewAssignmentUi(assignment.first, assignment.second.joinToString(", "))
    } + classTeacherAssignments.values
}

internal fun staffFilterValuesFrom(row: StaffRowUi): Boolean {
    return row.status == StaffStatusUi.ACTIVE
}

internal fun sampleStaffRows(): List<StaffRowUi> {
    val featured = listOf(
        StaffRowUi(
            id = "staff-001",
            staffId = "STF001",
            staffName = "Sample Teacher",
            staffRole = StaffRoleUi.TEACHER,
            authRole = StaffAuthRoleUi.STAFF,
            mobileNumber = "9876543210",
            gender = StaffGenderUi.MALE,
            emailAddress = "teacher@example.com",
            status = StaffStatusUi.ACTIVE,
            createdAt = "15 May 2024, 10:30 AM",
            updatedAt = "28 May 2024, 04:45 PM"
        ),
        StaffRowUi(
            id = "staff-002",
            staffId = "STF002",
            staffName = "Anita Joseph",
            staffRole = StaffRoleUi.PRINCIPAL,
            authRole = StaffAuthRoleUi.ADMIN,
            mobileNumber = "9876543211",
            gender = StaffGenderUi.FEMALE,
            emailAddress = "anita.joseph@school.com",
            status = StaffStatusUi.ACTIVE,
            createdAt = "16 May 2024",
            updatedAt = "29 May 2024"
        ),
        StaffRowUi(
            id = "staff-003",
            staffId = "STF003",
            staffName = "Sandeep Hebbar",
            staffRole = StaffRoleUi.COORDINATOR,
            authRole = StaffAuthRoleUi.MANAGEMENT,
            mobileNumber = "9876543212",
            gender = StaffGenderUi.MALE,
            emailAddress = "sandeep.hebbar@school.com",
            status = StaffStatusUi.ACTIVE,
            createdAt = "17 May 2024",
            updatedAt = "30 May 2024"
        ),
        StaffRowUi(
            id = "staff-004",
            staffId = "STF004",
            staffName = "Meera Pai",
            staffRole = StaffRoleUi.LIBRARIAN,
            authRole = StaffAuthRoleUi.STAFF,
            mobileNumber = "9876543213",
            gender = StaffGenderUi.FEMALE,
            emailAddress = "meera.pai@school.com",
            status = StaffStatusUi.ACTIVE,
            createdAt = "18 May 2024",
            updatedAt = "31 May 2024"
        ),
        StaffRowUi(
            id = "staff-005",
            staffId = "STF005",
            staffName = "Kiran Rao",
            staffRole = StaffRoleUi.ACCOUNTANT,
            authRole = StaffAuthRoleUi.STAFF,
            mobileNumber = "9876543214",
            gender = StaffGenderUi.MALE,
            emailAddress = "kiran.rao@school.com",
            status = StaffStatusUi.INACTIVE,
            createdAt = "19 May 2024",
            updatedAt = "01 Jun 2024"
        ),
        StaffRowUi(
            id = "staff-006",
            staffId = "STF006",
            staffName = "Joseph Mathew",
            staffRole = StaffRoleUi.TEACHER,
            authRole = StaffAuthRoleUi.STAFF,
            mobileNumber = "9876543215",
            gender = StaffGenderUi.MALE,
            emailAddress = "joseph.mathew@school.com",
            status = StaffStatusUi.ACTIVE,
            createdAt = "20 May 2024",
            updatedAt = "02 Jun 2024"
        ),
        StaffRowUi(
            id = "staff-007",
            staffId = "STF007",
            staffName = "Sunita Rao",
            staffRole = StaffRoleUi.VICE_PRINCIPAL,
            authRole = StaffAuthRoleUi.ADMIN,
            mobileNumber = "9876543216",
            gender = StaffGenderUi.FEMALE,
            emailAddress = "sunita.rao@school.com",
            status = StaffStatusUi.ACTIVE,
            createdAt = "21 May 2024",
            updatedAt = "03 Jun 2024"
        )
    )

    val names = listOf(
        "Ananya Nair",
        "Arjun Sharma",
        "Bhavya Iyer",
        "Deepak Kulkarni",
        "Ishita Reddy",
        "Karthik Patil",
        "Meera Bose",
        "Nikhil Joshi",
        "Priya Menon",
        "Rahul Chatterjee",
        "Sahana Srinivasan",
        "Varun Gupta",
        "Vikram Verma",
        "Aditi Rao",
        "Rohit Mehta",
        "Shruti Pillai"
    )
    val roles = StaffRoleUi.entries
    val authRoles = StaffAuthRoleUi.entries
    val genders = StaffGenderUi.entries
    val inactiveSlots = setOf(12, 21, 33, 48, 67)

    val generated = (8..86).map { index ->
        val offset = index - 8
        val name = names[offset % names.size]
        val role = roles[offset % roles.size]
        val authRole = authRoles[(offset / 2) % authRoles.size]
        val gender = genders[offset % genders.size]
        val status = if (index in inactiveSlots) StaffStatusUi.INACTIVE else StaffStatusUi.ACTIVE
        val mobile = buildString {
            append("98765")
            append((4 + offset).toString().padStart(5, '0').takeLast(5))
        }.take(10)

        StaffRowUi(
            id = "staff-${index.toString().padStart(3, '0')}",
            staffId = "STF${index.toString().padStart(3, '0')}",
            staffName = name,
            staffRole = role,
            authRole = authRole,
            mobileNumber = mobile,
            gender = gender,
            emailAddress = name.lowercase().replace(" ", ".") + "@school.com",
            status = status,
            createdAt = "${10 + (offset % 18)} Jun 2024",
            updatedAt = "${11 + (offset % 18)} Jun 2024"
        )
    }

    return featured + generated
}

internal fun staffPage(state: StaffUiState): StaffPageUi {
    val window = state.paging.toWindow(state.staff.size)
    return StaffPageUi(
        items = state.staff,
        totalCount = window.totalCount,
        startIndex = window.startIndex,
        endIndex = window.endIndex,
        pageCount = window.pageCount
    )
}

private fun staffComparator(field: StaffSortField, ascending: Boolean): Comparator<StaffRowUi> {
    val comparator = when (field) {
        StaffSortField.STAFF_ID -> compareBySmartText<StaffRowUi> { it.staffId }
        StaffSortField.STAFF_NAME -> compareBySmartText<StaffRowUi> { it.staffName }
        StaffSortField.STAFF_ROLE -> compareBySmartText<StaffRowUi> { it.roleTitle.ifBlank { staffRoleLabel(it.staffRole) } }
        StaffSortField.AUTH_ROLE -> compareBySmartText<StaffRowUi> {
            staffAuthRoleDisplayLabel(it.authRoleCode, it.authRole)
        }
        StaffSortField.MOBILE_NUMBER -> compareBySmartText<StaffRowUi> { it.mobileNumber }
        StaffSortField.STATUS -> compareBy<StaffRowUi> { it.status.ordinal }
    }
    return if (ascending) comparator else comparator.reversed()
}

private fun compareStaffDateFilter(rowDate: String, filterDate: String, lowerBound: Boolean): Boolean {
    val comparableFilter = staffDateComparable(filterDate) ?: return true
    val comparableRow = staffDateComparable(rowDate) ?: return false
    return if (lowerBound) comparableRow >= comparableFilter else comparableRow <= comparableFilter
}

private fun staffDateComparable(value: String): Int? {
    val trimmed = value.trim()
    if (trimmed.isBlank()) return null
    val datePart = trimmed.substringBefore(",").trim()
    val parts = datePart.split(" ").filter { it.isNotBlank() }
    if (parts.size != 3) return null
    val day = parts[0].toIntOrNull() ?: return null
    val month = monthNumber(parts[1]) ?: return null
    val year = parts[2].toIntOrNull() ?: return null
    if (day !in 1..31) return null
    return year * 10_000 + month * 100 + day
}

private fun monthNumber(value: String): Int? = when (value.lowercase()) {
    "jan", "january" -> 1
    "feb", "february" -> 2
    "mar", "march" -> 3
    "apr", "april" -> 4
    "may" -> 5
    "jun", "june" -> 6
    "jul", "july" -> 7
    "aug", "august" -> 8
    "sep", "sept", "september" -> 9
    "oct", "october" -> 10
    "nov", "november" -> 11
    "dec", "december" -> 12
    else -> null
}
